"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import format from "date-fns/format";
import { default as dynamicImport } from "next/dynamic";

import Toggle from "@/components/Toggle";
import ContentReveal from "@/components/ContentReveal";
import CurrencySelector from "@/components/CurrencySelector";
import QuarterSelector from "@/components/QuarterSelector";
import CheckBox from "@/components/CheckBox";

import useCurrency from "@/hooks/useCurrency";
import useSizeUnit from "@/hooks/useSizeUnit";

import { getApiClient } from "@ide/shared/lib/apiClient";

import { PROPERTY_SEARCH_TYPES, PROPERTY_SEARCH_AVAILABILITIES } from "@/config";

// TODO loading or other placeholder
const Select = dynamicImport(() => import("react-select"));
const Creatable = dynamicImport(() => import("react-select/creatable"));
const Slider = dynamicImport(() => import("@/components/Slider"));

const titleClassName = "text-lg font-medium";

const RangeInput = ({ onChange, currency, label, value }) => (
  <label className="px-2 py-1.5 flex flex-col border border-gray-300 rounded-lg">
    <span className="text-xs text-gray-700">{label}</span>
    <div className="h-0.5"></div>
    <div className="flex items-center w-full">
      <span className="mr-1.5">{currency}</span>
      <input
        type="text"
        className="w-full bg-transparent outline-hidden"
        onChange={onChange}
        value={value}
      />
    </div>
  </label>
);

const PropertyFilters = ({
  setFieldValue = () => {},
  values = {},
  errors = {},
  filters = [
    "availability",
    "unit_types",
    "number_of_bedrooms",
    "completion_max",
    "size",
    "price_range",
    "payment_plan",
    "distances",
    "investment",
  ],
}) => {
  const { data: developers, isSuccess: isDevelopersDataSuccess } = useQuery({
    queryKey: ["developers"],
    queryFn: async () => getApiClient().get("/api/developers"),
  });

  const { currentCurrencySymbol } = useCurrency();
  const { currentSizeUnit } = useSizeUnit();

  return (
    <div className="flex flex-col gap-8">
      {filters.includes("availability") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Availability</h3>

          {PROPERTY_SEARCH_AVAILABILITIES.map(({ value, label }) => (
            <label className="flex gap-2 cursor-pointer" key={`availability-${value}`}>
              <CheckBox
                name="types"
                checked={values?.availability?.includes(value)}
                onChange={() => {
                  if (values.availability.includes(value)) {
                    setFieldValue(
                      "availability",
                      values.availability.filter((a) => a !== value),
                    );
                  } else {
                    setFieldValue("availability", [...values.availability, value]);
                  }
                }}
              />

              <span>{label}</span>
            </label>
          ))}

          {errors.availability && (
            <div className="p-2 rounded-sm border border-red-300 bg-rose-100 text-sm text-red-700">
              <p>{errors.availability}</p>
            </div>
          )}
        </div>
      )}

      {filters.includes("unit_types") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Types</h3>

          <div className="grid grid-cols-2 gap-2">
            {PROPERTY_SEARCH_TYPES.map(({ value, label }) => (
              <label className="flex gap-2" key={`unit_types-${value}`}>
                <CheckBox
                  name="types"
                  checked={values?.unit_types?.includes(value)}
                  onChange={() => {
                    if (values.unit_types.includes(value)) {
                      setFieldValue(
                        "unit_types",
                        values.unit_types.filter((a) => a !== value),
                      );
                    } else {
                      setFieldValue("unit_types", [...values.unit_types, value]);
                    }
                  }}
                />

                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {filters.includes("completion_max") && (
        <div>
          <h3 className={titleClassName}>Latest Completion</h3>

          <QuarterSelector
            value={values?.completion_max}
            onChange={(date) => setFieldValue("completion_max", date)}
          />
        </div>
      )}

      {filters.includes("number_of_bedrooms") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Bedrooms</h3>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 0, label: "0 (Studio)" },
              { value: 1, label: 1 },
              { value: 2, label: 2 },
              { value: 3, label: 3 },
              { value: 4, label: 4 },
              { value: 5, label: 5 },
              { value: "6+", label: "6+" },
            ].map(({ value, label }) => (
              <div key={`number_of_bedrooms:${value}`}>
                <button
                  className={cx(
                    "min-w-16 px-3 py-1.5 flex justify-center items-center border rounded-full text-sm whitespace-nowrap",
                    {
                      "border-gray-200": !values?.number_of_bedrooms?.includes(value),
                      "border-black bg-black text-white font-medium":
                        (values?.number_of_bedrooms?.length === 0 && value === null) ||
                        values?.number_of_bedrooms?.includes(value),
                    },
                  )}
                  onClick={() => {
                    if (values.number_of_bedrooms.includes(value)) {
                      setFieldValue(
                        "number_of_bedrooms",
                        values.number_of_bedrooms.filter((a) => a !== value),
                      );
                    } else {
                      setFieldValue("number_of_bedrooms", [...values.number_of_bedrooms, value]);
                    }
                  }}
                >
                  {label}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* {filters.includes("size") && (
        <>
          <h3 className={titleClassName}>Size</h3>
          <div className="h-2"></div>

          <div>
            <h4>Minimum size</h4>

            <Slider
              onChange={(val) => setFieldValue("sizeMin", val === 500 ? undefined : val)}
              value={values.sizeMin ?? 500}
              min={500}
              max={15000}
              step={(() => {
                // TODO this one bugs out smhw
                // console.log(values.sizeMin);
                // if (values.sizeMin >= 10000) {
                //   return 1000;
                // }

                if (values.sizeMin >= 5000) {
                  return 500;
                }

                if (values.sizeMin >= 1000) {
                  return 250;
                }

                return 100;
              })()}
              label={values.sizeMin ? `${values.sizeMin} ${currentSizeUnit}` : "Any"}
              labelClassName={cx("min-w-28", {
                "text-gray-500": !values.sizeMin,
              })}
            />

            <div className="h-4"></div>
          </div>
        </>
      )} */}

      {filters.includes("price_range") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Price range</h3>
          <div className="flex items-center flex-nowrap gap-4">
            <div className="flex-1">
              <RangeInput
                onChange={({ target }) => setFieldValue("price_min", target.value)}
                currency={currentCurrencySymbol}
                label="Minimum"
                value={values.price_min}
              />
            </div>

            <div className="w-4 shrink-0 h-px bg-black"></div>

            <div className="flex-1">
              <RangeInput
                onChange={({ target }) => setFieldValue("price_max", target.value)}
                currency={currentCurrencySymbol}
                label="Maximum"
                value={values.price_max}
              />
            </div>

            <div className="relative z-20">
              <CurrencySelector />
            </div>
          </div>
        </div>
      )}

      {filters.includes("payment_plan") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Payment plan</h3>
          <div className="grid grid-cols-1 gap-2 gap-y-3">
            <label className="h-6 flex items-center cursor-pointer">
              <CheckBox
                checked={values.is_payment_plan_post_handover}
                onChange={({ target }) =>
                  setFieldValue("is_payment_plan_post_handover", target.checked || undefined)
                }
                className="mr-2"
              />
              Post-handover
            </label>

            <label className="h-6 flex items-center cursor-pointer">
              <CheckBox
                checked={values.payment_plan_triggers.includes("progress_construction")}
                onChange={() => {
                  if (values.payment_plan_triggers.includes("progress_construction")) {
                    setFieldValue(
                      "payment_plan_triggers",
                      values.payment_plan_triggers.filter((t) => t !== "progress_construction"),
                    );
                  } else {
                    setFieldValue("payment_plan_triggers", [
                      ...values.payment_plan_triggers,
                      "progress_construction",
                    ]);
                  }
                }}
                className="mr-2"
              />
              Construction-milestone based
            </label>
          </div>
        </div>
      )}

      {filters.includes("financial") && (
        <>
          <h3>Financial estimations</h3>
          <div className="h-4"></div>
        </>
      )}

      {filters.includes("developers") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Developers</h3>
          <div className="grid grid-cols-1 gap-2 gap-y-3 md:grid-cols-2">
            {isDevelopersDataSuccess &&
              developers
                .sort((a, b) => b.projectsCount - a.projectsCount)
                .slice(0, 10)
                .map(({ name, slug }) => (
                  <label className="h-6 flex items-center cursor-pointer" key={`developer:${slug}`}>
                    <CheckBox className="mr-2" />
                    {name}
                  </label>
                ))}
          </div>
        </div>
      )}

      {filters.includes("distances") && (
        <div className="flex flex-col gap-2">
          <h3 className={titleClassName}>Distances</h3>
          <div>
            <h4>Maximum distance to closest Metro station</h4>

            <Slider
              onChange={(val) => setFieldValue("maxDistanceToMetro", val === 11 ? undefined : val)}
              value={values.maxDistanceToMetro ?? 11}
              min={1}
              max={11}
              reverse
              label={values.maxDistanceToMetro ? `${values.maxDistanceToMetro} km` : "Any"}
              labelClassName={cx("min-w-16", {
                "text-gray-500": !values.maxDistanceToMetro,
              })}
            />
          </div>

          <div>
            <h4>Maximum distance to closest Golf course</h4>
            <Slider
              className="horizontal-slider reverse"
              onChange={(val) =>
                setFieldValue("max_distance_to_golf_course", val === 11 ? undefined : val)
              }
              value={values.max_distance_to_golf_course ?? 11}
              min={1}
              max={11}
              reverse
              label={
                values.max_distance_to_golf_course
                  ? `${values.max_distance_to_golf_course} km`
                  : "Any"
              }
              labelClassName={cx("min-w-16", {
                "text-gray-500": !values.max_distance_to_golf_course,
              })}
            />
          </div>
        </div>
      )}

      {filters.includes("investment") && (
        <div>
          <h3 className={titleClassName}>Investment</h3>

          <div>
            <h4>Minimum guaranteed ROI after handover</h4>
            <Slider
              className="horizontal-slider"
              onChange={(val) =>
                setFieldValue("min_fixed_guaranteed_roi", val === 2 ? undefined : val)
              }
              value={values.min_fixed_guaranteed_roi ?? 2}
              min={2}
              max={10}
              label={
                values.min_fixed_guaranteed_roi ? `${values.min_fixed_guaranteed_roi}%` : "Any"
              }
              labelClassName={cx("min-w-16", {
                "text-gray-500": !values.min_fixed_guaranteed_roi,
              })}
            />

            <div className="h-4"></div>
          </div>

          <div>
            <h4>Minimum guaranteed ROI duration after handover</h4>
            <Slider
              className="horizontal-slider"
              onChange={(val) =>
                setFieldValue("min_fixed_guaranteed_roi_duration", val === 2 ? undefined : val)
              }
              value={values.min_fixed_guaranteed_roi_duration ?? 2}
              min={2}
              max={10}
              label={
                values.min_fixed_guaranteed_roi_duration
                  ? `${values.min_fixed_guaranteed_roi_duration} years`
                  : "Any"
              }
              labelClassName={cx("min-w-16", {
                "text-gray-500": !values.min_fixed_guaranteed_roi_duration,
              })}
            />

            <div className="h-4"></div>
          </div>
        </div>
      )}

      {/* <ul>metro, beach, burj khalifa, burj al arab, palm, airports, golf clubs</ul> */}
    </div>
  );
};

export default PropertyFilters;
